import { useQuery } from '@tanstack/react-query';
import BACKEND_URL from 'constants/backendUrl';
import { Civility } from 'core/enums/civility';
import { TaskSubject } from 'core/enums/taskSubject';
import EventType from 'core/enums/eventType';
import { GET } from 'utils/http';

export type ContactResult = {
    id: string;
    firstName?: string;
    lastName: string;
    job?: string;
    company?: string;
    civility: Civility;
    comments?: string;
    createdDate: Date;
    tags?: { id: string; name: string }[];
};

export type TaskResult = {
    id: string;
    subject: TaskSubject;
    customSubject?: string;
    comments?: string;
    markAsCompleteComment?: string;
    deadline: Date;
    createdDate: Date;
    contact: {
        id: string;
        firstName?: string;
        lastName: string;
        civility: Civility;
    };
};

export type EventResult = {
    id: string;
    subject: string;
    type: EventType;
    mediaDetails?: string;
    comments?: string;
    markAsCompleteComment?: string;
    date: Date;
    createdDate: Date;
    contact: {
        id: string;
        firstName?: string;
        lastName: string;
        civility: Civility;
    };
};

export type ApplicationResult = {
    id: string;
    company: string;
    job: string;
    reference?: string;
    place?: string;
    sourceDetails?: string;
    comment?: string;
    description?: string;
    createdDate: Date;
    tags?: { id: string; name: string }[];
};

export type GlobalSearchResults = {
    contacts: ContactResult[];
    tasks: TaskResult[];
    events: EventResult[];
    applications: ApplicationResult[];
};

const EMPTY_RESULTS: GlobalSearchResults = { contacts: [], tasks: [], events: [], applications: [] };

const fetchGlobalSearch = async (query: string): Promise<GlobalSearchResults> => {
    const params = new URLSearchParams({ q: query });
    return (await GET(`${BACKEND_URL}/api/search?${params}`)) as GlobalSearchResults;
};

export const useGlobalSearchQuery = (query: string, enabled: boolean) =>
    useQuery({
        queryKey: ['globalSearch', query],
        queryFn: () => fetchGlobalSearch(query),
        enabled: enabled && query.trim().length >= 3,
        placeholderData: EMPTY_RESULTS,
    });
